import { Box, useTheme } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TaskDescriptionProps {
  description: string;
  // Live coding floats a video widget over the bottom of this pane, so it
  // defaults to deep trailing space. Callers without an overlay pass their own.
  bottomPadding?: number;
}

export const TaskDescription: React.FC<TaskDescriptionProps> = ({
  description,
  bottomPadding = 25,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          p: 3,
          pb: bottomPadding,
          minHeight: '100%',
          '& h1': {
            color: theme.palette.text.primary,
            marginTop: 0,
            marginBottom: theme.spacing(3),
            fontSize: '1.5rem',
            fontWeight: 600,
          },
          '& h2': {
            color: theme.palette.text.primary,
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(2),
            fontSize: '1.25rem',
            fontWeight: 600,
          },
          '& p': {
            color: theme.palette.text.primary,
            lineHeight: 1.6,
            marginBottom: theme.spacing(2),
          },
          '& pre': {
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(2),
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            overflow: 'auto',
            margin: `${theme.spacing(1)} 0`,
          },
          '& code': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.05)',
            padding: '2px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            color: theme.palette.text.primary,
          },
          '& pre code': {
            backgroundColor: 'transparent',
            padding: 0,
            color: theme.palette.text.primary,
          },
          '& table': {
            borderCollapse: 'collapse',
            margin: `${theme.spacing(2)} 0`,
          },
          '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(0.75, 1.5),
            textAlign: 'left',
            color: theme.palette.text.primary,
            fontSize: '0.9rem',
          },
          '& th': {
            backgroundColor: theme.palette.action.hover,
            fontWeight: 600,
          },
          '& ul': {
            paddingLeft: theme.spacing(2),
            margin: 0,
          },
          '& li': {
            marginBottom: theme.spacing(1),
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            color: theme.palette.text.primary,
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{description}</ReactMarkdown>
      </Box>
    </Box>
  );
};
